<template>
  <!-- Desktop: content only (AuthLayout provides the branding panel) -->
  <div v-if="isAuthDesktop" class="flex flex-col flex-1 items-center justify-center overflow-y-auto">
    <div class="w-full max-w-2xl px-10 py-10">

      <!-- Back link -->
      <div class="mb-4">
        <router-link
          :to="{ name: backRoute }"
          class="text-[13px] text-muted-foreground hover:text-foreground transition-colors">
          &larr; {{ $t(backLabelKey) }}
        </router-link>
      </div>

      <!-- Headline -->
      <h1 class="text-[24px] font-bold text-foreground mb-2">
        {{ $t(headlineKey) }}
      </h1>

      <!-- Stepper -->
      <ConnectionStepper :progress="progress" />

      <!-- Content -->
      <div class="pt-6">
        <router-view />
      </div>
    </div>
  </div>

  <!-- Mobile: full layout (unchanged) -->
  <div v-else id="CenterSigninShell" class="h-screen w-screen flex flex-col overflow-hidden relative shell-bg">

    <!-- Decorative background -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-20 -right-8 w-56 h-[380px] rotate-[20deg] rounded-[36px] shell-deco-top"></div>
      <div class="absolute -bottom-16 -left-12 w-72 h-72 rounded-full shell-deco-bottom"></div>
    </div>

    <div class="flex flex-col flex-1 min-h-0 relative z-10">

      <!-- Header: back + title -->
      <div class="pt-safe flex items-center gap-3 px-safe pb-3">
        <button @click="router.back()"
          class="w-10 h-10 rounded-xl flex items-center justify-center bg-card shell-back-btn active:scale-95 transition-transform duration-150 shrink-0">
          <ArrowLeftIcon class="h-[18px] w-[18px] text-foreground" />
        </button>
        <p class="text-[16px] font-bold text-foreground">
          {{ $t(headlineKey) }}
        </p>
      </div>

      <!-- Stepper right under header -->
      <ConnectionStepper :progress="progress" />

      <!-- Content fills remaining space -->
      <div class="flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden pt-6 px-safe-flush">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, provide, inject, type Ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ArrowLeftIcon } from '@heroicons/vue/24/outline';
import ConnectionStepper from '../components/ConnectionStepper.vue';

const router = useRouter();
const route = useRoute();

// Injected from AuthLayout — false if not provided (e.g. direct access)
const isAuthDesktop = inject<Ref<boolean>>('isAuthDesktop', ref(false));

// Context system — determines wording
const validContexts = ['onboarding', 'add', 'reconnect'] as const;
type SigninContext = typeof validContexts[number];
const rawCtx = route.query.context as string;
const signinContext = ref<SigninContext>(
  validContexts.includes(rawCtx as SigninContext) ? rawCtx as SigninContext : 'add'
);

const headlineKey = computed(() =>
  signinContext.value === 'reconnect' ? 'centersignin.headline_reconnect' : 'centersignin.headline'
);

const backRoute = computed(() => {
  if (signinContext.value === 'reconnect') return 'Dashboard';
  if (signinContext.value === 'onboarding') return 'NoCenterPage';
  return 'Profile';
});

const backLabelKey = computed(() => {
  if (signinContext.value === 'reconnect') return 'centersignin.back_to_dashboard';
  if (signinContext.value === 'onboarding') return 'centersignin.back_to_home';
  return 'centersignin.back_to_profile';
});

const progress = ref<1 | 2 | 3 | 4>(1);
provide('connectionProgress', progress);
</script>

<style scoped>
/* Light mode */
.shell-bg {
  background: linear-gradient(175deg, #FFFFFF 0%, #F0F7FA 40%, #F4F4F4 100%);
}
.shell-deco-top {
  background: linear-gradient(180deg, rgba(6, 200, 255, 0.09) 0%, transparent 55%);
}
.shell-deco-bottom {
  background: radial-gradient(circle, rgba(6, 200, 255, 0.06) 0%, transparent 60%);
}
.shell-back-btn {
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

/* Dark mode */
:is(.dark *).shell-bg {
  background: linear-gradient(175deg, #1B1C1E 0%, #1e2528 40%, #1B1C1E 100%);
}
:is(.dark *).shell-deco-top {
  background: linear-gradient(180deg, rgba(6, 200, 255, 0.15) 0%, transparent 55%);
}
:is(.dark *).shell-deco-bottom {
  background: radial-gradient(circle, rgba(6, 200, 255, 0.04) 0%, transparent 50%);
}
:is(.dark *).shell-back-btn {
  border: 1px solid rgba(6, 200, 255, 0.10);
  box-shadow: 0 1px 6px rgba(6, 200, 255, 0.05);
}
</style>
